import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Spinner } from '../../components/common/Spinner';
import { adminService } from '../../services/adminService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { Mail, Phone, Globe, Wallet, Ticket, ArrowDownCircle } from 'lucide-react';
import api from '../../config/api';

type Tab = 'tickets' | 'transactions' | 'withdrawals';

export const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('tickets');
  const [ticketPage, setTicketPage] = useState(1);
  const [txPage, setTxPage] = useState(1);
  const [wdPage, setWdPage] = useState(1);

  const { data: userData, isLoading } = useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => adminService.getUser(id!),
    enabled: !!id,
  }) as { data: any; isLoading: boolean };

  const { data: ticketsData } = useQuery({
    queryKey: ['admin', 'users', id, 'tickets', ticketPage],
    queryFn: () => api.get(`/admin/users/${id}/tickets`, { params: { page: ticketPage, limit: 20 } }).then(r => r.data),
    enabled: !!id && tab === 'tickets',
  });

  const { data: txData } = useQuery({
    queryKey: ['admin', 'users', id, 'transactions', txPage],
    queryFn: () => api.get(`/admin/users/${id}/transactions`, { params: { page: txPage, limit: 20 } }).then(r => r.data),
    enabled: !!id && tab === 'transactions',
  });

  const { data: wdData } = useQuery({
    queryKey: ['admin', 'users', id, 'withdrawals', wdPage],
    queryFn: () => api.get(`/admin/users/${id}/withdrawals`, { params: { page: wdPage, limit: 20 } }).then(r => r.data),
    enabled: !!id && tab === 'withdrawals',
  });

  if (isLoading) return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;

  const user = userData?.data;
  if (!user) return <AdminLayout><p className="text-gray-400 dark:text-gray-500 text-center py-12">User not found.</p></AdminLayout>;

  const currency = user.country?.currency || 'USD';
  const tickets = ticketsData?.data?.tickets || [];
  const ticketsPag = ticketsData?.data?.pagination;
  const transactions = txData?.data?.transactions || [];
  const txPag = txData?.data?.pagination;
  const withdrawals = wdData?.data?.withdrawals || [];
  const wdPag = wdData?.data?.pagination;

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: 'tickets', label: 'Tickets', icon: Ticket },
    { id: 'transactions', label: 'Transactions', icon: Wallet },
    { id: 'withdrawals', label: 'Withdrawals', icon: ArrowDownCircle },
  ];

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{user.firstName} {user.lastName}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">User profile and history</p>
      </div>

      {/* User info cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { icon: Mail, label: 'Email', value: user.email },
          { icon: Phone, label: 'Phone', value: user.phone || '—' },
          { icon: Globe, label: 'Country', value: `${user.country?.name || user.countryCode} (${currency})` },
          { icon: Wallet, label: 'Wallet Balance', value: user.wallet ? formatCurrency(user.wallet.balance, currency) : '—' },
        ].map(({ icon: Icon, label, value }) => (
          <Card key={label}>
            <Icon className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mb-2" />
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5 break-all">{value}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Role', value: user.role, badge: true, variant: user.role === 'ADMIN' ? 'info' : user.role === 'AGENT' ? 'success' : 'default' },
          { label: 'Status', value: user.isActive !== false ? 'Active' : 'Inactive', badge: true, variant: user.isActive !== false ? 'success' : 'danger' },
          { label: 'Email Verified', value: user.emailVerified ? 'Yes' : 'No' },
          { label: 'Joined', value: formatDate(user.createdAt) },
        ].map(({ label, value, badge, variant }: any) => (
          <div key={label} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
            {badge ? <Badge variant={variant}>{value}</Badge> : <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{value}</p>}
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-100 dark:border-gray-800">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* Tickets Tab */}
      {tab === 'tickets' && (
        <Card>
          {tickets.length === 0 ? (
            <p className="text-gray-400 dark:text-gray-500 text-center py-8 text-sm">No tickets found.</p>
          ) : (
            <>
              <div className="space-y-2">
                {tickets.map((t: any) => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.raffle?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">#{t.ticketNumber}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(t.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(t.purchasePrice, t.raffle?.currency || currency)}</p>
                      <Badge variant={t.status === 'WON' ? 'success' : t.status === 'LOST' ? 'danger' : t.status === 'REFUNDED' ? 'warning' : 'default'}>{t.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
              {ticketsPag && ticketsPag.pages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-4">
                  <Button variant="outline" size="sm" disabled={ticketPage === 1} onClick={() => setTicketPage(ticketPage - 1)}>Previous</Button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Page {ticketPage} of {ticketsPag.pages}</span>
                  <Button variant="outline" size="sm" disabled={ticketPage === ticketsPag.pages} onClick={() => setTicketPage(ticketPage + 1)}>Next</Button>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Transactions Tab */}
      {tab === 'transactions' && (
        <Card>
          {transactions.length === 0 ? (
            <p className="text-gray-400 dark:text-gray-500 text-center py-8 text-sm">No transactions found.</p>
          ) : (
            <>
              <div className="space-y-2">
                {transactions.map((t: any) => (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.description}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(t.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-bold ${t.type === 'CREDIT' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {t.type === 'CREDIT' ? '+' : '-'}{formatCurrency(t.amount, currency)}
                      </span>
                      <p><Badge variant={t.status === 'COMPLETED' ? 'success' : t.status === 'FAILED' ? 'danger' : 'warning'}>{t.status}</Badge></p>
                    </div>
                  </div>
                ))}
              </div>
              {txPag && txPag.pages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-4">
                  <Button variant="outline" size="sm" disabled={txPage === 1} onClick={() => setTxPage(txPage - 1)}>Previous</Button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Page {txPage} of {txPag.pages}</span>
                  <Button variant="outline" size="sm" disabled={txPage === txPag.pages} onClick={() => setTxPage(txPage + 1)}>Next</Button>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Withdrawals Tab */}
      {tab === 'withdrawals' && (
        <Card>
          {withdrawals.length === 0 ? (
            <p className="text-gray-400 dark:text-gray-500 text-center py-8 text-sm">No withdrawals found.</p>
          ) : (
            <>
              <div className="space-y-2">
                {withdrawals.map((w: any) => (
                  <div key={w.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div>
                      <Badge variant={w.status === 'COMPLETED' ? 'success' : w.status === 'FAILED' ? 'danger' : 'warning'}>{w.status}</Badge>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{w.method}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(w.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-red-600">-{formatCurrency(w.amount, currency)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Fee: {formatCurrency(w.fee, currency)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Net: {formatCurrency(w.netAmount, currency)}</p>
                    </div>
                  </div>
                ))}
              </div>
              {wdPag && wdPag.pages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-4">
                  <Button variant="outline" size="sm" disabled={wdPage === 1} onClick={() => setWdPage(wdPage - 1)}>Previous</Button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Page {wdPage} of {wdPag.pages}</span>
                  <Button variant="outline" size="sm" disabled={wdPage === wdPag.pages} onClick={() => setWdPage(wdPage + 1)}>Next</Button>
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </AdminLayout>
  );
};
